import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { TASK_ROW_HEIGHT } from "@/common/constants";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface NumberSelectProps {
    value: number | null;
    onChange: (value: number | null) => void;
    trigger: (content: React.ReactNode) => React.ReactElement;
}

interface NumberFormData {
    number: string;
}

export function NumberSelect({ value, onChange, trigger }: NumberSelectProps) {
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const { register, handleSubmit, reset, setValue } = useForm<NumberFormData>({
        defaultValues: {
            number: value?.toString() || "",
        },
    });

    const onSubmit = (data: NumberFormData) => {
        const numValue = data.number === "" ? null : Number(data.number);
        if (!Number.isNaN(numValue as number) || numValue === null) {
            onChange(numValue);
            setOpen(false);
        }
    };

    const handleCancel = () => {
        reset({ number: value?.toString() || "" });
        setOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit(onSubmit)();
        } else if (e.key === "Escape") {
            e.preventDefault();
            handleCancel();
        }
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (newOpen) {
            setValue("number", value?.toString() || "");
            // Use setTimeout to ensure the input is fully rendered and focused
            setTimeout(() => {
                inputRef.current?.select();
            }, 0);
        }
        setOpen(newOpen);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger render={trigger(value?.toString())} />
            <PopoverContent sideOffset={-TASK_ROW_HEIGHT} align="end" className="w-full p-0 max-w-80">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Input
                        type="text"
                        {...register("number", {
                            setValueAs: (value) => value,
                        })}
                        ref={(e) => {
                            register("number").ref(e);
                            inputRef.current = e;
                        }}
                        onKeyDown={handleKeyDown}
                        onBlur={handleSubmit(onSubmit)}
                        autoFocus
                    />
                </form>
            </PopoverContent>
        </Popover>
    );
}
